import { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  List,
  ListItem,
} from '@chakra-ui/react';
import { useTable, useSortBy } from 'react-table';
import { format } from 'date-fns';
import { Users } from '../../../../../api/user';

const TestResultsRegisteredUsersTable = ({ registered }) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = useMemo(
    () => [
      {
        Header: 'מספר',
        accessor: 'id',
      },
      {
        Header: 'מספר טלפון',
        accessor: 'phoneNumber',
      },
      {
        Header: 'שם מלא',
        accessor: (row) =>
          row.fullName || (row.firstName && row.lastName ? `${row.firstName} ${row.lastName}` : ''),
      },
      {
        Header: 'תעודת זהות',
        accessor: (row) => row.details?.govId || '',
      },
      {
        Header: 'סטטוס',
        accessor: 'status',
        Cell: ({ value }) => <Text fontSize="sm">{Users.statusText(value)}</Text>,
      },
      {
        Header: 'חברה',
        accessor: 'companyId',
      },
      {
        Header: 'שכר שעתי',
        accessor: 'hourlyGrossRate',
        width: '100px',
        Cell: ({ value }) => {
          if (value && value > 0) {
            return (
              <Text fontSize="sm" textAlign="center">
                ₪{value.toFixed(2)}
              </Text>
            );
          }

          return (
            <Text fontSize="sm" textAlign="center" color="gray.500">
              לא זמין
            </Text>
          );
        },
      },
      {
        Header: 'משמרות',
        accessor: 'integrationData',
        Cell: ({ value }) => {
          if (!value) return null;

          if (!value.success) {
            return (
              <Text fontSize="sm" color="red.500">
                האינטגרציה נכשלה
              </Text>
            );
          }

          if (value?.success && value?.timeReporting?.length > 0) {
            return (
              <List spacing={1} fontSize="sm">
                {value.timeReporting.map((report, idx) => (
                  <ListItem key={idx}>
                    {format(new Date(report.shiftDate), 'dd/MM/yyyy')} -{' '}
                    {Math.round(report.workingHours * 10) / 10} שעות
                  </ListItem>
                ))}
              </List>
            );
          }
          return <Text fontSize="sm">אין משמרות</Text>;
        },
      },
      {
        Header: 'שגיאות',
        accessor: (row) => row.integrationData?.error || '',
        Cell: ({ value }) => (
          <Text fontSize="sm" color={value ? 'red.500' : 'secondaryGray.900'}>
            {value || ''}
          </Text>
        ),
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: registered || [],
    },
    useSortBy,
  );

  return (
    <Box>
      <Text fontWeight="bold" fontSize="lg" mb={3}>
        משתמשים רשומים
      </Text>
      <Box maxH="400px" overflowY="auto" border="1px" borderColor={borderColor} borderRadius="md">
        {registered?.length > 0 ? (
          <Table {...getTableProps()} variant="simple" size="sm">
            <Thead position="sticky" top={0} bg="white" zIndex="1">
              {headerGroups.map((headerGroup, idx) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                  {headerGroup.headers.map((column, idx) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={idx}
                      borderColor={borderColor}
                      textAlign="right"
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {column.render('Header')}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {!!rows?.length &&
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <Tr {...row.getRowProps()} key={row.id}>
                      {row.cells.map((cell, idx) => (
                        <Td
                          {...cell.getCellProps()}
                          key={idx}
                          fontSize={{ sm: '14px' }}
                          color={textColor}
                          borderColor={borderColor}
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
        ) : (
          <Text p={4} textAlign="center">
            אין משתמשים רשומים להצגה
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default TestResultsRegisteredUsersTable;
